import Button from "@bmi-digital/components/button";
import Add from "@bmi-digital/components/icon/Add";
import Remove from "@bmi-digital/components/icon/Remove";
import ShoppingCart from "@bmi-digital/components/icon/ShoppingCart";
import Section from "@bmi-digital/components/section";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { Link } from "gatsby";
import React, { useEffect, useState } from "react";
import {
  ACTION_TYPES,
  createSample,
  useBasketContext
} from "../contexts/SampleBasketContext";
import { Product } from "../types/pim";
import { createActionLabel } from "../utils/createActionLabelForAnalytics";
import { getPathWithCountryCode } from "../utils/path";
import { useSiteContext } from "./Site";
import {
  StyledSampleOrderContainer,
  classes
} from "./styles/SampleOrderSection.styles";

const SampleOrderSection = ({
  isSampleOrderAllowed,
  product,
  maximumSamples,
  path,
  actionLabel
}: {
  isSampleOrderAllowed: boolean;
  product: Product;
  maximumSamples?: number;
  path?: string;
  actionLabel?: string;
}) => {
  const { basketState, basketDispatch } = useBasketContext();
  //actions
  const addToBasket = (product: Product) => {
    basketDispatch({
      type: ACTION_TYPES.BASKET_ADD,
      payload: createSample(product)
    });
  };
  const removeFromBasket = (product: Product) => {
    basketDispatch({
      type: ACTION_TYPES.BASKET_REMOVE,
      payload: createSample(product)
    });
  };

  const { getMicroCopy } = useSiteContext();

  const sampleMessage = () => {
    if (isBasketFull) {
      return getMicroCopy(microCopy.PDP_OVERVIEW_SAMPLE_LIMIT_REACHED_MESSAGE);
    }
    if (isSampleOrderAllowed) {
      return getMicroCopy(microCopy.PDP_OVERVIEW_CAN_ADD_MORE_MESSAGE);
    }
    return getMicroCopy(microCopy.PDP_OVERVIEW_CAN_ADD_OTHER_MESSAGE);
  };
  const [hasSampleInTheBasket, setHasSampleInTheBasket] = useState(false);
  const [isBasketFull, setIsBasketFull] = useState(false);
  const [basketHasProducts, setBasketHasProducts] = useState(
    basketState.products.length > 0
  );
  const { countryCode } = useSiteContext();
  useEffect(() => {
    if (product) {
      setHasSampleInTheBasket(
        basketState.products.filter(
          (brasketProduct) => brasketProduct.code === product.code
        ).length > 0
      );
    }
    setIsBasketFull(
      maximumSamples ? basketState?.products?.length >= maximumSamples : false
    );
    setBasketHasProducts(basketState?.products?.length > 0);
  }, [basketState, maximumSamples, product]);

  const gtmAction =
    actionLabel ||
    createActionLabel(
      product.name,
      product.colour,
      product.textureFamily,
      product.measurements?.label
    );

  const sampleBasketUrl =
    basketHasProducts &&
    path &&
    getPathWithCountryCode(countryCode, path).replace(/\/+/gi, "/");

  const removeSampleButtonLabel = getMicroCopy(
    microCopy.PDP_OVERVIEW_REMOVE_SAMPLE
  );
  const addSampleButtonLabel = getMicroCopy(microCopy.PDP_OVERVIEW_ADD_SAMPLE);

  return (
    (isSampleOrderAllowed || basketHasProducts) && (
      <div>
        <StyledSampleOrderContainer>
          <Section
            backgroundColor="white"
            spacing="none"
            hasNoPadding
            id={`sample-order`}
          >
            {basketHasProducts && (
              <div className={classes["sample-message"]}>{sampleMessage()}</div>
            )}

            <div className={classes["buttons-container"]}>
              {isSampleOrderAllowed ? (
                hasSampleInTheBasket ? (
                  <Button
                    endIcon={<Remove />}
                    onClick={() => removeFromBasket(product)}
                    variant="text"
                    data-testid={"remove-from-basket-button"}
                    gtm={{
                      id: "cta-click1",
                      label: removeSampleButtonLabel
                    }}
                  >
                    {removeSampleButtonLabel}
                  </Button>
                ) : !isBasketFull ? (
                  <Button
                    endIcon={<Add />}
                    onClick={() => addToBasket(product)}
                    gtm={{
                      id: "cta-click1-samples-ordering",
                      label: addSampleButtonLabel,
                      action: gtmAction
                    }}
                    data-testid={"add-to-basket-button"}
                  >
                    {addSampleButtonLabel}
                  </Button>
                ) : undefined
              ) : undefined}
              {sampleBasketUrl && (
                <Button
                  className={classes["complete-order"]}
                  endIcon={<ShoppingCart />}
                  variant="outlined"
                  to={sampleBasketUrl}
                  component={Link}
                  gtm={{
                    id: "cta-click1",
                    action: sampleBasketUrl,
                    label: path
                  }}
                  data-testid={"complete-order-basket-button"}
                >
                  {getMicroCopy(microCopy.PDP_OVERVIEW_COMPLETE_SAMPLE_ORDER)}
                </Button>
              )}
            </div>
          </Section>
        </StyledSampleOrderContainer>
      </div>
    )
  );
};

export default SampleOrderSection;
